import React from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyPermission } from '../utils/permissions';
import { Box, Typography, Alert, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const ProtectedRoute = ({ children, permissions = [], requiredRole = null, requiredFeature = null, fallback = null }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  let features = null;
  try {
    const context = useOutletContext();
    features = context?.features;
  } catch (e) {
    // Context may not be available outside layout
  }

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check feature-gating if requiredFeature is specified
  if (requiredFeature && features && !features[requiredFeature]) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Feature Disabled
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This feature is not included in your organization's subscription plan. Please contact your administrator to upgrade.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Alert>
      </Box>
    );
  }

  // Check role-based access if requiredRole is specified
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This page is only accessible to {requiredRole.replace('_', ' ')} users. Your current role is {user?.role?.replace('_', ' ')}.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Alert>
      </Box>
    );
  }

  // If no permissions required, allow access
  if (!permissions || permissions.length === 0) {
    return children;
  }

  // Check if user has required permissions
  const hasAccess = hasAnyPermission(user?.role, permissions);

  if (!hasAccess) {
    // Show custom fallback or default access denied message
    if (fallback) {
      return fallback;
    }

    return (
      <Box sx={{ p: 3, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You don't have permission to access this page. Please contact your administrator if you believe this is an error.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Alert>
      </Box>
    );
  }

  return children;
};

export default ProtectedRoute;